import { useQuery } from "@tanstack/react-query";
import { TenantConfig } from "../../types/tenant";
import { logger } from "../../utils/logger";
import defaultConfig from "../../../default-tenant-config.json";
import { getConfig } from "../../config/environment";
import { TENANT_CONFIG_QUERY_KEY } from "./tenantConfigCache";

// Define the type for sort options
interface SortOption {
  label: string;
  value: string;
}

// Default config with type assertion
const defaultTenantConfig: TenantConfig = {
  id: "",
  ...defaultConfig,
  // Convert sortOptions to string array as required by TenantConfig
  search: {
    ...defaultConfig.search,
    sortOptions: defaultConfig.search.sortOptions.map(
      (opt: SortOption) => opt.value
    ),
  },
  // Remove extra features not in TenantConfig
  features: {
    savedProperties: defaultConfig.features.savedProperties,
    advancedFilters: defaultConfig.features.advancedFilters,
    mapView: defaultConfig.features.mapView,
    streetView: defaultConfig.features.streetView,
  },
  // Initialize empty subscribedMLS array
  subscribedMLS: [],
};

interface UseTenantConfigOptions {
  hostname?: string;
  skipCache?: boolean;
}

/**
 * Custom hook to fetch tenant configuration using React Query
 * Includes caching with a 24-hour stale time
 */
// `org_id` is persisted by URLParamsProvider under this key (shared across tabs),
// so a new tab can resolve the tenant on localhost without the param in its URL.
const URL_PARAMS_STORAGE_KEY = "speculo_url_params";
const readStoredOrgId = (): string => {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(URL_PARAMS_STORAGE_KEY);
    if (!raw) return "";
    const data = JSON.parse(raw) as { params?: { org_id?: string } };
    return (data?.params?.org_id || "").trim();
  } catch {
    return "";
  }
};

export const useTenantConfig = (options: UseTenantConfigOptions = {}) => {
  const { hostname: testHostname, skipCache = false } = options;

  // Get hostname from window or use test hostname
  const hostname =
    testHostname ||
    (typeof window !== "undefined" ? window.location.hostname : "");

  // Local dev: `window.location.hostname` is `localhost`, which the tenant-config
  // service can't map to a tenant. On localhost the org id resolves the tenant via
  // `/config?org_id=` — from the `?org_id=` URL param, else the persisted URL-params
  // store (URLParamsProvider keeps `org_id` sticky there, shared across tabs), else
  // the VITE_TENANT_ORG_ID env var. Gated to localhost; production uses hostname.
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("localhost") ||
      window.location.hostname === "127.0.0.1");

  let overrideOrgId = "";
  if (isLocalhost && typeof window !== "undefined") {
    overrideOrgId =
      (new URLSearchParams(window.location.search).get("org_id") || "").trim() ||
      readStoredOrgId();
  }
  if (!overrideOrgId) {
    overrideOrgId = (import.meta.env.VITE_TENANT_ORG_ID || "").trim();
  }

  const API_URL = getConfig().cloudRunServices.tenantConfig;
  const configQuery = overrideOrgId
    ? `org_id=${encodeURIComponent(overrideOrgId)}`
    : `hostname=${encodeURIComponent(hostname)}`;

  return useQuery({
    queryKey: [TENANT_CONFIG_QUERY_KEY, overrideOrgId || hostname],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/config?${configQuery}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          // Don't include credentials - not needed for public API
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch config: ${response.status} ${response.statusText}`
        );
      }

      const apiConfig = await response.json();

      // The Spanner tenant-config service returns logo/favicon as flat top-level
      // URL strings, not nested under `branding` (colors/fonts already arrive
      // under branding). Map them into the { url, width, height } objects the UI
      // reads. The service doesn't send dimensions, so fall back to the defaults.
      const apiBranding = apiConfig.branding || {};
      const defaultLogo = defaultTenantConfig.branding.logo;
      const logoUrl =
        (typeof apiConfig.logo === "string" && apiConfig.logo) ||
        apiBranding.logo?.url ||
        defaultLogo?.url;
      const faviconUrl =
        (typeof apiConfig.favicon === "string" && apiConfig.favicon) ||
        apiBranding.favicon?.url ||
        defaultTenantConfig.branding.favicon?.url;

      // The Spanner service returns orgData with curated keys (name/contactEmail/
      // contactPhone/address/socialLinks); the app reads the legacy raw shape
      // (org_name/email/org_phone/client_address/org_links). Normalize so every
      // orgData consumer (footer links, Terms/Privacy/DMCA, MLS compliance, SEO
      // location) resolves whichever shape the deployed service returns. Fields
      // with no Spanner source (primary_contact, phoneNumbers) pass through as-is.
      const apiOrgData = apiConfig.orgData || {};
      const orgData = {
        ...apiOrgData,
        id: apiOrgData.id || apiConfig.id,
        org_name: apiOrgData.org_name || apiOrgData.name || apiConfig.name,
        email: apiOrgData.email || apiOrgData.contactEmail,
        org_phone: apiOrgData.org_phone || apiOrgData.contactPhone,
        client_address: apiOrgData.client_address || apiOrgData.address,
        org_links: apiOrgData.org_links || apiOrgData.socialLinks,
        brokerage_name: apiOrgData.brokerage_name || apiBranding.brokerageName,
      };

      // Transform API response to match our TenantConfig interface. Every branding
      // field is sourced from the tenant config (colors/fonts merged onto defaults
      // so a partial config never drops a key; logo/favicon mapped from top-level).
      const mergedConfig: TenantConfig = {
        ...defaultTenantConfig,
        id: apiConfig.id || "default",
        name: apiConfig.name || "Speculo",
        // SPEC-2232: this object is assembled field-by-field, so the per-org
        // consent style must be copied explicitly or it is dropped before the
        // squeeze/register consent gate can read it.
        idxConsentStyle: apiConfig.idxConsentStyle,
        branding: {
          ...defaultTenantConfig.branding,
          ...apiBranding,
          colors: {
            ...defaultTenantConfig.branding.colors,
            ...(apiBranding.colors || {}),
          },
          fonts: {
            ...defaultTenantConfig.branding.fonts,
            ...(apiBranding.fonts || {}),
          },
          logo: logoUrl
            ? {
                url: logoUrl,
                width: apiBranding.logo?.width ?? defaultLogo?.width ?? 150,
                height: apiBranding.logo?.height ?? defaultLogo?.height ?? 50,
                placement:
                  apiBranding.logo?.placement ?? defaultLogo?.placement,
              }
            : defaultLogo,
          ...(faviconUrl && {
            favicon: {
              url: faviconUrl,
              width: apiBranding.favicon?.width ?? 32,
              height: apiBranding.favicon?.height ?? 32,
            },
          }),
        },
        analytics: apiConfig.analytics || defaultTenantConfig.analytics,
        features: apiConfig.features || defaultTenantConfig.features,
        search: {
          ...defaultTenantConfig.search,
          ...(apiConfig.search || {}),
          // Ensure sortOptions is always an array of strings
          sortOptions:
            apiConfig.search?.sortOptions ||
            defaultTenantConfig.search.sortOptions,
        },
        subscribedMLS: apiConfig.subscribedMLS || [],
        subscribedCities: apiConfig.subscribedCities || [],
        orgData,
      };

      return mergedConfig;
    },
    // Bounded staleness: subscribedCities gates which IDX landing pages resolve,
    // so an open storefront session must not serve a day-stale subscription list
    // (a city unsubscribed in Client-Admin would otherwise keep its landing page
    // live for up to a day). Revalidate every 10 min and whenever the visitor
    // returns to the tab; invalidateTenantConfig() forces it on demand.
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 24 * 60 * 60 * 1000, // 24 hours (1 day)
    refetchOnWindowFocus: true,
    refetchOnMount: !skipCache,
    retry: 3,
  });
};
